<template>
  <RevButton
    v-if="!isMarketDisabledForPaidMethods"
    :class="`!rounded-xs place-items-center px-6 ${className}`"
    :disabled="isDisabled"
    size="small"
    variant="secondary"
    @click="emit('selected', marketWithSubMarket)"
  >
    <span class="flex">
      <Flag
        :aria-label="`flag-${marketWithSubMarket.market}`"
        class="mr-4 shrink-0"
        :country-code="marketWithSubMarket.market"
        size="xl"
      />
      {{ marketWithSubMarket.market }}
    </span>
  </RevButton>

  <RevTooltip v-else position="top">
    <template #trigger="{ hide, show }">
      <FlagDisplayer
        :disabled="true"
        :market="marketWithSubMarket.market"
        @focusin="show"
        @focusout="hide"
        @mouseleave="hide"
        @mouseover="show"
      />
    </template>
    {{ i18n(translations.errorPaidMethodTimeNotFastEnough) }}
  </RevTooltip>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { MarketWithSubMarkets } from '@backmarket/http-api/src/api-specs-shipping/rules'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { tw } from '@backmarket/utils/string/tw'
import { RevButton } from '@ds/components/Button'
import { RevTooltip } from '@ds/components/Tooltip'

import type { ShippingCategoryGroupMetadata } from '../../CategoryList.types.js'
import { getCarrier } from '../../CategoryList.utils'
import FlagDisplayer from '../FlagDisplayer/FlagDisplayer.vue'
import { MIN_SHIPPING_TIME } from '../RuleDrawer/RuleDrawer.constants'
import type { FormMethodValues } from '../RuleDrawer/RuleDrawer.types.js'
import { getMinShippingTime } from '../RuleDrawer/RuleDrawer.utils.js'
import translations from '../RuleDrawer/components/RuleFormFields/RuleFormFields.translations'

const i18n = useI18n()

const props = defineProps<{
  marketWithSubMarket: MarketWithSubMarkets
  categoryGroupMetadata: ShippingCategoryGroupMetadata
  currentSelection: MarketWithSubMarkets[]
  currentMethods: FormMethodValues[]
  otherMethods: FormMethodValues[]
  isFree: boolean
  shouldShowCollectionPointOptions?: boolean
}>()

const emit = defineEmits(['selected'])

const isMarketDisabledForPaidMethods = computed(() => {
  if (props.isFree) {
    return false
  }

  const minimumShippingTimeForMarket = getMinShippingTime(props.otherMethods, [
    props.marketWithSubMarket.market,
  ])

  return (
    minimumShippingTimeForMarket &&
    minimumShippingTimeForMarket <= MIN_SHIPPING_TIME
  )
})

const hasCollectionPointAlreadySelected = computed(() => {
  return props.currentMethods.some(({ shippingProvider }) => {
    const carrier = getCarrier(
      props.categoryGroupMetadata.shippingMethods.providerOptions,
      shippingProvider,
    )

    return carrier?.collectionPoint
  })
})

const isDisabled = computed(() => {
  if (props.shouldShowCollectionPointOptions) {
    return hasCollectionPointAlreadySelected.value
  }

  const currentMethodsWithoutCollectionPoint = props.currentMethods.filter(
    ({ shippingProvider }) => {
      const carrier = getCarrier(
        props.categoryGroupMetadata.shippingMethods.providerOptions,
        shippingProvider,
      )

      return !carrier?.collectionPoint
    },
  )

  return (
    isMarketDisabledForPaidMethods.value ||
    (props.isFree &&
      currentMethodsWithoutCollectionPoint.some(({ markets: currentMarkets }) =>
        currentMarkets.some(
          ({ market: currentMarket }) =>
            currentMarket === props.marketWithSubMarket.market,
        ),
      ))
  )
})

const isSelected = computed(() => {
  return props.currentSelection.some(
    (currentSelectedMarketWithSubMarket) =>
      currentSelectedMarketWithSubMarket.market ===
      props.marketWithSubMarket.market,
  )
})

const className = computed(() => {
  if (isDisabled.value) {
    return tw`border-0 bg-static-default-hi-isDisabled`
  }

  return isSelected.value
    ? tw`!border-static-success-hi !bg-static-success-mid !text-static-success-hi`
    : tw`!border-static-default-mid`
})
</script>
